import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader } from "ui/components/Loader";
import { formatCurrency, formatDateParam } from "helpers";
import { Routes as FinanceRoutes } from "ui/modules/finance/navigation";
import { getFleetVehicleExpensesAPI } from "services/api/fleet/vehicles/expenses/api";
import { initialPagination } from "ui/components/Table/consts";
import {
  FleetVehicleExpenseItem,
  FleetVehicleExpenseRow,
} from "services/api/fleet/vehicles/expenses/types";
import VehicleExpenseChart, {
  ExpenseChartBucket,
} from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleExpenseSearchPage/components/VehicleExpenseStats/components/VehicleExpenseChart/VehicleExpenseChart";
import TableIcon from "ui/components/Table/TableSearch/components/TableIcon";
import DashboardTooltip, { TooltipDivider, TooltipRow, TooltipTitle } from "../../../common/DashboardTooltip";
import { Tabs } from "ui/components/Tabs";
import CardHeader, { DeltaBadge } from "../../../common/CardHeader";
import { negative } from "../../../common/dashboardPalette";
import { useDashboardDateRange } from "../../../../context/DashboardDateRangeContext";

const MONTH_NAMES = ["Sij", "Velj", "Ožu", "Tra", "Svi", "Lip", "Srp", "Kol", "Ruj", "Lis", "Stu", "Pro"];
const MONTH_WINDOW = 12;
const TOP_VEHICLES = 10;

// Prozirni gradient ispune po indeksu tipa — okrenut (jak na kraju, uz vertikalni border) -> blijedi prema početku.
// Pune klase u stringovima da Tailwind JIT vidi sve nijanse.
const TYPE_FILLS = [
  "bg-gradient-to-l from-sky-500/30 to-sky-500/[0.02] border-r-2 border-sky-500 group-hover/row:from-sky-500/45",
  "bg-gradient-to-l from-violet-500/30 to-violet-500/[0.02] border-r-2 border-violet-500 group-hover/row:from-violet-500/45",
  "bg-gradient-to-l from-emerald-500/30 to-emerald-500/[0.02] border-r-2 border-emerald-500 group-hover/row:from-emerald-500/45",
  "bg-gradient-to-l from-amber-500/40 to-amber-500/[0.02] border-r-2 border-amber-500 group-hover/row:from-amber-500/55",
  "bg-gradient-to-l from-rose-500/30 to-rose-500/[0.02] border-r-2 border-rose-500 group-hover/row:from-rose-500/45",
  "bg-gradient-to-l from-cyan-500/30 to-cyan-500/[0.02] border-r-2 border-cyan-500 group-hover/row:from-cyan-500/45",
  "bg-gradient-to-l from-indigo-500/30 to-indigo-500/[0.02] border-r-2 border-indigo-500 group-hover/row:from-indigo-500/45",
  "bg-gradient-to-l from-teal-500/30 to-teal-500/[0.02] border-r-2 border-teal-500 group-hover/row:from-teal-500/45",
  "bg-gradient-to-l from-orange-500/40 to-orange-500/[0.02] border-r-2 border-orange-500 group-hover/row:from-orange-500/55",
  "bg-gradient-to-l from-fuchsia-500/30 to-fuchsia-500/[0.02] border-r-2 border-fuchsia-500 group-hover/row:from-fuchsia-500/45",
];
const TYPE_RINGS = [
  "hover:ring-sky-400", "hover:ring-violet-400", "hover:ring-emerald-400", "hover:ring-amber-400", "hover:ring-rose-400",
  "hover:ring-cyan-400", "hover:ring-indigo-400", "hover:ring-teal-400", "hover:ring-orange-400", "hover:ring-fuchsia-400",
];
const VEHICLE_FILL = "bg-gradient-to-l from-rose-500/30 to-rose-500/[0.02] border-r-2 border-rose-500 group-hover/row:from-rose-500/45";

interface MonthBucket {
  month: string;
  monthLabel: string;
  amount: number;
  cumulative: number;
  items: FleetVehicleExpenseItem[];
  date: Date;
}

const ExpensesStatsTab: React.FC = () => {
  const navigate = useNavigate();
  const { range } = useDashboardDateRange();
  const [expenses, setExpenses] = useState<FleetVehicleExpenseRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Drill-down na pretragu troškova (tab "Pretraga") s trenutnim datumskim rasponom widgeta.
  const rangeSearchParams = () => {
    const params = new URLSearchParams({ tab: "search" });
    const from = formatDateParam(range.from);
    const to = formatDateParam(range.to);
    if (from) params.set("expenseDateFrom", from);
    if (to) params.set("expenseDateTo", to);
    return params;
  };

  // Klik na vrstu troška -> pretraga filtrirana po tipu + rasponu.
  const handleTypeClick = (typeId: string) => {
    const params = rangeSearchParams();
    params.set("expenseTypeId", typeId);
    navigate(`${FinanceRoutes.ExpenseSearchPageRouter}?${params.toString()}`);
  };

  // Klik na vozilo -> pretraga filtrirana po marki/modelu/tipu + rasponu (popunjava vidljive filtere).
  const handleVehicleClick = (
    manufacturerId: string | null,
    modelId: string | null,
    typeId: string | null
  ) => {
    const params = rangeSearchParams();
    if (manufacturerId) params.set("vehicleManufacturerId", manufacturerId);
    if (modelId) params.set("vehicleModelId", modelId);
    if (typeId) params.set("vehicleTypeId", typeId);
    navigate(`${FinanceRoutes.ExpenseSearchPageRouter}?${params.toString()}`);
  };

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    (async () => {
      try {
        const result = await getFleetVehicleExpensesAPI({
          ...initialPagination,
          pageSize: 1000,
        });
        if (!cancelled) setExpenses(result.data);
      } catch {
        if (!cancelled) setExpenses([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Prozor razdoblja poravnat na cijele mjesece — isti za graf i za breakdown tabove.
  // Graf grupira po kalendarskom mjesecu, pa bi filtriranje breakdowna po točnom
  // datumu (npr. `to` = danas) izbacilo troškove kasnije u tekućem mjesecu i ukupni
  // iznos u zaglavlju se ne bi slagao s udjelima ispod njega.
  const { startMonth, endMonthExclusive, monthCount, previousStart } = useMemo(() => {
    const now = new Date();
    const start = range.from
      ? new Date(range.from.getFullYear(), range.from.getMonth(), 1)
      : new Date(now.getFullYear(), now.getMonth() - (MONTH_WINDOW - 1), 1);
    const rawEnd = range.to
      ? new Date(range.to.getFullYear(), range.to.getMonth() + 1, 1)
      : new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const rawMonths =
      (rawEnd.getFullYear() - start.getFullYear()) * 12 + (rawEnd.getMonth() - start.getMonth());
    const count = Math.min(Math.max(rawMonths, 1), 24);

    return {
      startMonth: start,
      endMonthExclusive: new Date(start.getFullYear(), start.getMonth() + count, 1),
      monthCount: count,
      previousStart: new Date(start.getFullYear(), start.getMonth() - count, 1),
    };
  }, [range]);

  const inWindow = useCallback(
    (date: Date) => date >= startMonth && date < endMonthExclusive,
    [startMonth, endMonthExclusive]
  );

  // Breakdown po vrsti troška (unutar odabranog razdoblja).
  const expenseTypeBreakdown = useMemo(() => {
    const map = new Map<string, { id: string; name: string; amount: number; count: number }>();
    expenses.forEach((e) => {
      if (!e.expenseDate || !inWindow(new Date(e.expenseDate))) return;
      const id = e.expenseTypeId ?? "other";
      const name = e.expenseTypeName ?? "Ostalo";
      const existing = map.get(id) ?? { id, name, amount: 0, count: 0 };
      existing.amount += e.amount ?? 0;
      existing.count += 1;
      map.set(id, existing);
    });
    return Array.from(map.values()).sort((a, b) => b.amount - a.amount);
  }, [expenses, inWindow]);

  // Top vozila po troškovima (unutar odabranog razdoblja).
  const topVehicles = useMemo(() => {
    const map = new Map<string, { manufacturerId: string | null; modelId: string | null; typeId: string | null; name: string; plate: string | null; image: string | null; initials: string; amount: number; count: number }>();
    expenses.forEach((e) => {
      if (!e.expenseDate || !inWindow(new Date(e.expenseDate))) return;
      const id = e.vehicle?.id;
      // Opći troškovi tvrtke nemaju vozilo pa ne ulaze u ovu ljestvicu.
      if (!id || !e.vehicle) return;
      const make = e.vehicle.vehicleManufacturer?.name ?? "";
      const model = e.vehicle.vehicleModel?.name ?? "";
      const name = `${make} ${model}`.trim() || "Vozilo";
      const plate = e.vehicle.registrationPlate ?? null;
      const image = e.vehicle.defaultImage ?? null;
      const initials = (make.charAt(0) || "?").toUpperCase();
      const manufacturerId = e.vehicle.vehicleManufacturer?.id ?? null;
      const modelId = e.vehicle.vehicleModel?.id ?? null;
      const typeId = e.vehicle.vehicleType?.id ?? null;
      const existing = map.get(id) ?? { manufacturerId, modelId, typeId, name, plate, image, initials, amount: 0, count: 0 };
      existing.amount += e.amount ?? 0;
      existing.count += 1;
      map.set(id, existing);
    });
    return Array.from(map.values()).sort((a, b) => b.amount - a.amount).slice(0, TOP_VEHICLES);
  }, [expenses, inWindow]);

  const { buckets, totalAmount, previousTotal, percentageChange, windowStart, windowEndDate } = useMemo(() => {
    // Inicijaliziraj mjesece u prozoru da svi imaju 0 ako nema podataka.
    const monthMap = new Map<string, MonthBucket>();
    for (let i = 0; i < monthCount; i++) {
      const d = new Date(startMonth.getFullYear(), startMonth.getMonth() + i, 1);
      const key = `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
      monthMap.set(key, {
        month: key,
        monthLabel: MONTH_NAMES[d.getMonth()],
        amount: 0,
        cumulative: 0,
        items: [],
        date: d,
      });
    }

    let prevPeriodTotal = 0;

    expenses.forEach((e) => {
      if (!e.expenseDate) return;
      const date = new Date(e.expenseDate);
      const key = `${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
      const bucket = monthMap.get(key);
      const amount = e.amount ?? 0;

      if (bucket) {
        bucket.amount += amount;
        bucket.items.push({
          name: e.name ?? "-",
          date: date,
          amount,
          cumulative: 0, // popunjava se nakon sortiranja
          invoiceNumber: null,
        });
      } else if (date >= previousStart && date < startMonth) {
        prevPeriodTotal += amount;
      }
    });

    // Sortiraj po mjesecu uzlazno i izračunaj kumulativ.
    const sorted = Array.from(monthMap.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
    let runningCumulative = 0;
    sorted.forEach((b) => {
      b.items.sort((x, y) => new Date(x.date).getTime() - new Date(y.date).getTime());
      let runningInBucket = runningCumulative;
      b.items.forEach((it) => {
        runningInBucket += it.amount;
        it.cumulative = runningInBucket;
      });
      runningCumulative += b.amount;
      b.cumulative = runningCumulative;
    });

    const total = runningCumulative;
    const change = prevPeriodTotal > 0 ? ((total - prevPeriodTotal) / prevPeriodTotal) * 100 : 0;

    const lastBucket = sorted[sorted.length - 1];
    const winEnd = new Date(lastBucket.date.getFullYear(), lastBucket.date.getMonth() + 1, 1);

    return {
      buckets: sorted,
      totalAmount: total,
      previousTotal: prevPeriodTotal,
      percentageChange: change,
      windowStart: sorted[0].date,
      windowEndDate: winEnd,
    };
  }, [expenses, startMonth, monthCount, previousStart]);

  if (isLoading) {
    return (
      <div className="relative h-full min-h-[300px]">
        <Loader isLoading={true}> </Loader>
      </div>
    );
  }

  const chartBuckets: ExpenseChartBucket[] = buckets.map((b) => ({
    label: b.monthLabel,
    items: b.items,
    cumulative: b.cumulative,
  }));

  // Klik na točku grafa -> pretraga troškova za taj kalendarski mjesec.
  const handleMonthClick = (bucketIndex: number) => {
    const bucket = buckets[bucketIndex];
    if (!bucket) return;
    const monthStart = new Date(bucket.date.getFullYear(), bucket.date.getMonth(), 1);
    const monthEnd = new Date(bucket.date.getFullYear(), bucket.date.getMonth() + 1, 0);
    const params = new URLSearchParams({ tab: "search" });
    params.set("expenseDateFrom", formatDateParam(monthStart)!);
    params.set("expenseDateTo", formatDateParam(monthEnd)!);
    navigate(`${FinanceRoutes.ExpenseSearchPageRouter}?${params.toString()}`);
  };

  const tooltipTitle = (b: ExpenseChartBucket) => {
    const idx = chartBuckets.indexOf(b);
    const matched = buckets[idx];
    if (!matched) return b.label;
    const yyyy = matched.month.split("/")[1] ?? "";
    return `${b.label} ${yyyy}`;
  };

  return (
    <div className="rounded-sm px-4 py-5 bg-white flex flex-col h-full">
      {/* Tabovi na vrhu (kao na Vozilima) — header/tekst je unutar svakog taba.
          Prvi (kumulativni) se renderira odmah, ostali tek na klik (Tabs montira samo aktivni sadržaj). */}
      <Tabs
        variant="pill"
        tabs={[
          {
            label: "Kumulativno",
            content: (
              <>
                <CardHeader
                  title="Kumulativni pregled"
                  subtitle="Za odabrano razdoblje"
                  className="mt-4 mb-2"
                  right={
                    <DashboardTooltip
                      trigger={
                        <div className="flex flex-col items-end cursor-help gap-1">
                          <span className={`text-2xl font-bold ${negative.text}`}>
                            −{formatCurrency.format(totalAmount)}
                          </span>
                          <DeltaBadge value={percentageChange} inverted />
                        </div>
                      }
                    >
                      <TooltipTitle>Pregled troškova</TooltipTitle>
                      <TooltipRow label="Prethodno razdoblje" value={formatCurrency.format(previousTotal)} emphasis="muted" />
                      <TooltipRow label="Odabrano razdoblje" value={formatCurrency.format(totalAmount)} />
                      <TooltipDivider />
                      <TooltipRow
                        label="Promjena"
                        value={`${percentageChange >= 0 ? "+" : ""}${percentageChange.toFixed(1)}%`}
                        emphasis={percentageChange >= 0 ? "negative" : "positive"}
                      />
                    </DashboardTooltip>
                  }
                />
                <div className="flex-1 flex flex-col min-h-[320px]">
                  <VehicleExpenseChart
                    buckets={chartBuckets}
                    windowStart={windowStart}
                    windowEndDate={windowEndDate}
                    priceHistory={[]}
                    bucketTooltipTitle={tooltipTitle}
                    onBucketClick={handleMonthClick}
                  />
                </div>
              </>
            ),
          },
          {
            label: "Po tipu troška",
            content: (
              <div className="pt-4">
                <CardHeader title="Po tipu troška" subtitle="Udio po tipu u odabranom razdoblju" />
                {expenseTypeBreakdown.length === 0 ? (
                  <div className="text-sm text-slate-400 py-10 text-center">Nema podataka</div>
                ) : (
                  <div className="space-y-2">
                    {expenseTypeBreakdown.map((cat, i) => {
                      const pct = totalAmount > 0 ? (cat.amount / totalAmount) * 100 : 0;
                      return (
                        <div
                          key={cat.name}
                          className={`group/row relative rounded-sm ring-1 ring-slate-200 bg-white cursor-pointer transition-all hover:ring-2 hover:shadow-sm ${TYPE_RINGS[i % TYPE_RINGS.length]}`}
                          onClick={() => handleTypeClick(cat.id)}
                        >
                          <div className="absolute inset-0 overflow-hidden rounded-sm">
                            <div
                              className={`absolute inset-y-0 left-0 origin-left animate-growRight transition-all duration-500 ${TYPE_FILLS[i % TYPE_FILLS.length]}`}
                              style={{ width: `${Math.max(pct, 4)}%` }}
                            />
                          </div>
                          <div className="relative flex items-center justify-between gap-3 px-3 py-2">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-800 truncate">{cat.name}</div>
                              <div className="text-xs text-slate-500 truncate">{cat.count} {cat.count === 1 ? "stavka" : "stavki"}</div>
                            </div>
                            <div className="flex flex-col items-end flex-shrink-0">
                              <span className="text-sm font-bold text-slate-800">{formatCurrency.format(cat.amount)}</span>
                              <span className="text-xs text-slate-500">{pct.toFixed(0)}%</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ),
          },
          {
            label: `Top ${TOP_VEHICLES} vozila`,
            content: (
              <div className="pt-4">
                <CardHeader title={`Top ${TOP_VEHICLES} vozila po trošku`} subtitle="Najveći ukupni troškovi u razdoblju" />
                {topVehicles.length === 0 ? (
                  <div className="text-sm text-slate-400 py-10 text-center">Nema podataka</div>
                ) : (
                  <div className="space-y-2">
                    {topVehicles.map((v, i) => {
                      // Bar širina = % od najvećeg vozila (top 1) — vizualni indikator relativnog udjela.
                      const widthPct = topVehicles[0].amount > 0 ? (v.amount / topVehicles[0].amount) * 100 : 0;
                      return (
                        <div
                          key={`${v.name}-${i}`}
                          className="group/row relative rounded-sm ring-1 ring-slate-200 bg-white cursor-pointer transition-all hover:ring-2 hover:shadow-sm hover:ring-rose-400"
                          onClick={() => handleVehicleClick(v.manufacturerId, v.modelId, v.typeId)}
                        >
                          <div className="absolute inset-0 overflow-hidden rounded-sm">
                            <div
                              className={`absolute inset-y-0 left-0 origin-left animate-growRight transition-all duration-500 ${VEHICLE_FILL}`}
                              style={{ width: `${Math.max(widthPct, 5)}%` }}
                            />
                          </div>
                          <div className="relative flex items-center justify-between gap-3 px-3 py-2">
                            <div className="flex items-center gap-3 min-w-0">
                              <TableIcon imageUrl={v.image ?? undefined} initials={v.initials} />
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-slate-800 truncate">{v.name}</div>
                                <div className="text-xs text-slate-500 truncate">{v.plate ?? `${v.count} ${v.count === 1 ? "stavka" : "stavki"}`}</div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end flex-shrink-0">
                              <span className="text-sm font-bold text-slate-800">−{formatCurrency.format(v.amount)}</span>
                              <span className="text-xs text-slate-500">{v.count} st.</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default memo(ExpensesStatsTab);
